import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { fetchSeasonalRates, addSeasonalRate, deleteSeasonalRate } from "../../services/RateService";
import "./SeasonalRatesPanel.css";

// Formats a Date as "yyyy-MM-dd" using local Y/M/D (not toISOString, which
// converts to UTC and can shift the date across a day boundary depending on
// the browser's timezone) - same helper/reasoning as CreateBookingForm.jsx's.
// draft.fromDate/toDate and the API both stay on this plain ISO string; only
// the DatePicker itself needs a real Date object.
const toLocalDateString = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// "2026-10-31" -> "2026-11-01" - plain calendar-day arithmetic on the y-m-d
// components (the Date constructor normalizes an out-of-range day across a
// month/year boundary on its own), not raw Date + 86400000ms, which can
// land on the wrong local day across a DST transition.
const addOneDay = (isoDate) => {
  const [y, m, d] = isoDate.split("-").map(Number);
  return toLocalDateString(new Date(y, m - 1, d + 1));
};

// "2026-10-31" (what the API returns) -> "31/10/2026" - matches the
// dd/MM/yyyy format the DatePicker above both shows and accepts, so an
// existing override and the add-row sitting right below it don't show two
// different date formats at once. Plain string slicing, not a Date parse -
// the value is already an unambiguous y-m-d string, nothing to convert.
const formatDisplayDate = (isoDate) => {
  if (!isoDate) return isoDate;
  const [y, m, d] = isoDate.split("-");
  return `${d}/${m}/${y}`;
};

/**
 * Peak-season rate overrides for one RoomCategory - extracted out of
 * ManageResorts.jsx so both it and the centralized RateManagement.jsx page
 * can show/edit the same thing without duplicating this table+add-row UI.
 * Self-fetching (loads on mount given categoryId) rather than caching in a
 * parent-held map - simpler, and this panel is only ever mounted while its
 * row is expanded anyway.
 */
const SeasonalRatesPanel = ({ categoryId, showToast, initialFromDate, onRatesChanged, onClose }) => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  // initialFromDate lets a caller (e.g. clicking a date in RateManagement.jsx's
  // calendar grid) open this panel with the add-row already pointed at that
  // date, instead of staff having to re-pick it. toDate defaults one day
  // after it (a seasonal override is rarely just a single night), same
  // auto-advance CreateBookingForm.jsx's check-in/check-out pair does.
  const [draft, setDraft] = useState(() => {
    const fromDate = initialFromDate || "";
    return { fromDate, toDate: fromDate ? addOneDay(fromDate) : "", nightlyRate: "" };
  });

  const load = async () => {
    try {
      const data = await fetchSeasonalRates(categoryId);
      setRates(data || []);
    } catch (err) {
      console.error(err);
      showToast?.(err.message || "Failed to load seasonal rates", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  const handleAdd = async () => {
    if (!draft.fromDate || !draft.toDate || !draft.nightlyRate) {
      return showToast?.("Enter a from date, to date, and nightly rate", "warning");
    }
    try {
      await addSeasonalRate(categoryId, draft);
      setDraft({ fromDate: "", toDate: "", nightlyRate: "" });
      await load();
      onRatesChanged?.();
      showToast?.("Seasonal rate added", "success");
    } catch (err) {
      console.error(err);
      showToast?.(err.message || "Failed to add seasonal rate", "danger");
    }
  };

  const handleDelete = async (rateId) => {
    try {
      await deleteSeasonalRate(categoryId, rateId);
      setRates((prev) => prev.filter((r) => r.id !== rateId));
      onRatesChanged?.();
    } catch (err) {
      console.error(err);
      showToast?.(err.message || "Failed to delete seasonal rate", "danger");
    }
  };

  return (
    <div className="vt-seasonal-rates-panel">
      {onClose && (
        <div className="vt-seasonal-rates-panel-header">
          <span className="vt-seasonal-rates-panel-title">Seasonal Rates</span>
          <button type="button" className="vt-seasonal-rates-panel-close" onClick={onClose} title="Close" aria-label="Close">
            ✕
          </button>
        </div>
      )}
      {/* Grid, not a <table> - a table's auto column widths are computed
          per-element from its own header/data cell content, which can never
          line up with the add-row below (a separate element). Every row
          here, including the add-row, shares the exact same
          grid-template-columns (see the CSS), so From/To/Nightly Rate/the
          action column stay pixel-aligned top to bottom. */}
      <div className="vt-seasonal-rates-grid">
        <div className="vt-seasonal-rates-grid-row vt-seasonal-rates-grid-header">
          <div>From</div>
          <div>To</div>
          <div>Nightly Rate</div>
          <div />
        </div>
        {loading ? (
          <div className="vt-seasonal-rates-grid-row">
            <div className="field-hint vt-seasonal-rates-grid-span">Loading...</div>
          </div>
        ) : (
          <>
            {rates.map((rate) => (
              <div className="vt-seasonal-rates-grid-row" key={rate.id}>
                <div>{formatDisplayDate(rate.fromDate)}</div>
                <div>{formatDisplayDate(rate.toDate)}</div>
                <div>Rs. {rate.nightlyRate}</div>
                <div>
                  <button type="button" className="vt-btn vt-btn-danger" onClick={() => handleDelete(rate.id)}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
            {rates.length === 0 && (
              <div className="vt-seasonal-rates-grid-row">
                <div className="field-hint vt-seasonal-rates-grid-span">No seasonal overrides yet.</div>
              </div>
            )}
          </>
        )}
      </div>
      <div className="vt-seasonal-rates-add-row">
        <DatePicker
          className="date-picker"
          dateFormat="dd/MM/yyyy"
          placeholderText="dd/mm/yyyy"
          portalId="seasonal-rates-datepicker-portal"
          selected={draft.fromDate ? new Date(draft.fromDate) : null}
          onChange={(date) => {
            if (!date) {
              setDraft((prev) => ({ ...prev, fromDate: "" }));
              return;
            }
            const fromDate = toLocalDateString(date);
            setDraft((prev) => ({
              ...prev,
              fromDate,
              // Auto-advance To to the day after From whenever To is empty
              // or no longer after the new From - a To the guest already
              // deliberately pushed further out is left alone.
              toDate: !prev.toDate || prev.toDate <= fromDate ? addOneDay(fromDate) : prev.toDate,
            }));
          }}
        />
        <DatePicker
          className="date-picker"
          dateFormat="dd/MM/yyyy"
          placeholderText="dd/mm/yyyy"
          portalId="seasonal-rates-datepicker-portal"
          selected={draft.toDate ? new Date(draft.toDate) : null}
          onChange={(date) => setDraft({ ...draft, toDate: date ? toLocalDateString(date) : "" })}
        />
        <input
          type="number"
          min="0"
          placeholder="Nightly Rate"
          value={draft.nightlyRate}
          onChange={(e) => setDraft({ ...draft, nightlyRate: e.target.value })}
        />
        <button type="button" className="vt-btn vt-btn-primary" onClick={handleAdd}>
          + Add
        </button>
      </div>
    </div>
  );
};

export default SeasonalRatesPanel;
